import React, { useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import '../styles/DrawerForm.css';

export interface FormSliderProps<T> {
  workspaceId: string;
  itemId?: string;
  onSuccess: () => void;
  onCancel: () => void;
  apiEndpoint: string;
  initialFormData: T;
  children: (props: FormChildrenProps<T>) => ReactNode;
  validateForm?: (data: T) => string | null;
  transformSubmitData?: (data: T) => Record<string, any>;
  transformFetchData?: (data: Record<string, any>) => T;
  formId?: string;
  onErrorChange?: (error: string) => void;
  onLoadingChange?: (loading: boolean) => void;
}

export interface FormChildrenProps<T> {
  formData: T;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleFieldChange: (name: keyof T, value: unknown) => void;
  loading: boolean;
  error: string;
}

function FormSlider<T extends Record<string, any>>({
  workspaceId,
  itemId,
  onSuccess,
  onCancel,
  apiEndpoint,
  initialFormData,
  children,
  validateForm,
  transformSubmitData,
  transformFetchData,
  formId = 'form-slider',
  onErrorChange,
  onLoadingChange
}: FormSliderProps<T>) {
  const isEditMode = !!itemId;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<T>(initialFormData);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Notify parent of error changes
  useEffect(() => {
    if (onErrorChange) {
      onErrorChange(error);
    }
  }, [error, onErrorChange]);

  // Notify parent of loading changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);

  useEffect(() => {
    if (isEditMode && itemId) {
      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, isEditMode]);

  const fetchItem = async () => {
    try {
      setLoading(true);
      // Remove /api/v1 prefix if present (for backward compatibility)
      const cleanEndpoint = apiEndpoint.replace(/^\/api\/v1/, '');
      const url = cleanEndpoint.includes('{itemId}')
        ? cleanEndpoint.replace('{itemId}', itemId!)
        : `${cleanEndpoint}/${itemId}`;

      const response = await api.get(url);

      const data = transformFetchData
        ? transformFetchData(response.data)
        : response.data;

      setFormData(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch item');
      console.error('Error fetching item:', err);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const finalValue = (e.target as HTMLInputElement).type === 'checkbox'
      ? (e.target as HTMLInputElement).checked
      : value;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  const handleFieldChange = (name: keyof T, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate form
    if (validateForm) {
      const validationError = validateForm(formData);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    try {
      setLoading(true);

      const payload = transformSubmitData ? transformSubmitData(formData) : formData;
      // Remove /api/v1 prefix if present (for backward compatibility)
      const cleanEndpoint = apiEndpoint.replace(/^\/api\/v1/, '');

      if (isEditMode) {
        const url = cleanEndpoint.includes('{itemId}')
          ? cleanEndpoint.replace('{itemId}', itemId!)
          : `${cleanEndpoint}/${itemId}`;

        await api.put(url, payload);
      } else {
        await api.post(cleanEndpoint, payload);
      }

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} item`);
      console.error('Error submitting form:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode && isInitialLoad) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <form id={formId} className="drawer-form" onSubmit={handleSubmit}>
      {error && (
        <div className="form-error">
          {error}
        </div>
      )}

      {children({
        formData,
        handleInputChange,
        handleFieldChange,
        loading,
        error
      })}
    </form>
  );
}

export default FormSlider;
